import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

/*
 * Header nav-brand reveal.
 *
 * `.header-nav-brand` is a small copy of the page brand that lives inside the
 * sticky `.header-nav`. It must stay hidden while the nav is in normal flow and
 * reveal only once the nav docks to the top of the viewport (user has scrolled
 * down). The reveal is a left→right wipe: the clip-path mask expands rightward
 * while the inner link slides in from the left.
 *
 * The resting (hidden) state is set in CSS (interactions.css) so there is no
 * flash before this runs; here we tween from that state to the shown state.
 */

const DURATION = 0.5
const EASE = 'power2.inOut'

let trigger: ScrollTrigger | null = null
let tl: gsap.core.Timeline | null = null

export const destroyHeaderNavBrand = () => {
  if (trigger) {
    trigger.kill()
    trigger = null
  }
  if (tl) {
    tl.kill()
    tl = null
  }
}

export const initHeaderNavBrand = () => {
  destroyHeaderNavBrand()

  const nav = document.querySelector<HTMLElement>('.header-nav')
  if (!nav) return

  const brand = nav.querySelector<HTMLElement>('.header-nav-brand')
  const link = nav.querySelector<HTMLElement>('.header-nav-brand-link')
  if (!brand || !link) return

  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
  const duration = reduce ? 0 : DURATION

  // The brand rests at max-width:0 so it reserves no whitespace while hidden.
  // The inner link is `w-max`, so its box keeps its natural width even inside
  // the collapsed parent — measure it to know how far to grow the mask open.
  const naturalWidth = Math.ceil(link.getBoundingClientRect().width)

  // Inner link starts fully to the left, outside the collapsed mask.
  gsap.set(link, { xPercent: -100 })

  tl = gsap.timeline({ paused: true })
  tl.to(
    brand,
    { maxWidth: naturalWidth, clipPath: 'inset(0 0% 0 0)', duration, ease: EASE },
    0
  ).to(link, { xPercent: 0, duration, ease: EASE }, 0)

  // `end` is a fixed large offset, NOT 'max'. 'max' resolves to the page's
  // maximum scroll and enables GSAP's end-clamp (end = min(end, maxScroll)). On
  // a tall window the dev page's maxScroll falls below the nav's ~210px dock
  // point, so the clamped end lands below `start`, inverting the range — the
  // trigger then reports progress 1 at scroll 0 and the brand flashes in on load.
  // A '+=' offset never clamps, so the range can't invert; the brand stays
  // hidden until the nav genuinely docks, at any window height. We don't handle
  // onLeave, so an end past maxScroll is harmless.
  trigger = ScrollTrigger.create({
    trigger: nav,
    start: 'top top',
    end: '+=99999',
    onEnter: () => tl!.play(),
    onLeaveBack: () => tl!.reverse(),
  })

  // Handle an already-docked nav on load / after an ASwap navigation.
  if (nav.getBoundingClientRect().top <= 0) {
    tl.progress(1)
  }
}
